<template>
  <Menubar class="topbar" :model="items">
    <template #start>
      <div class="sidebar-header h-16 gap-3 cursor-pointer" @click="goToDashboard">
        <img :src="logoSrc" alt="Logo" class="sidebar-logo" />
        <div class="flex flex-col">
          <div class="text-lg font-extrabold text-green-600">Shops Infoboard</div>
          <div v-if="currentShopName" class="text-sm text-gray-600 dark:text-gray-300 font-medium">
            {{ currentShopName }}
          </div>
        </div>
      </div>
      <!-- Mobile toggle button -->
      <Button
        v-if="isMobile"
        icon="pi pi-bars"
        severity="secondary"
        text
        @click="toggleSidebar"
        class="mr-2 p-button-lg"
        v-tooltip.bottom="isSidebarVisible ? 'Hide sidebar' : 'Show sidebar'"
      />
      <!-- Desktop collapse button -->
      <Button
        v-if="!isMobile"
        :icon="isCollapsed ? 'pi pi-angle-double-right' : 'pi pi-angle-double-left'"
        text
        severity="secondary"
        @click="toggleCollapse"
        class="mr-2 ml-2.5 p-button-lg"
        v-tooltip.bottom="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      />
    </template>
    <template #end>
      <div class="flex">
      <Button :icon="currentTheme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" class="mr-2 p-button-lg" severity="secondary"  @click="toggleTheme" />
      <Menu ref="userMenu" :model="userMenuItems" :popup="true" />
      <Button v-if="isLogged" @click="toggleUserMenu" text class="">
        <span class="mr-2">{{ user.name || user.email }}</span>
        <i class="pi pi-user align-baseline text-lg"></i>
      </Button>
      </div>
    </template>
  </Menubar>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import Menu from 'primevue/menu';

export default {
  components: { Menubar, Button, Menu },
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();
    const userMenu = ref(null);

    // Get user data from store
    const user = computed(() => store.state.users.user);
    const isLogged = computed(() => store.state.users.user.isLogged);
    const currentShopName = computed(() => store.state.users.user.shopName);

    // Empty items for navbar (main menu moved to sidebar)
    const items = computed(() => []);

    // User menu items
    const userMenuItems = computed(() => [
      {
        label: 'Account',
        icon: 'pi pi-user',
        command: () => router.push('/account')
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => logout()
      }
    ]);

    const toggleUserMenu = (event) => {
      userMenu.value.toggle(event);
    };

    const logout = () => {
      store.commit('users/logoutUser');
      router.push('/');
    };

    const goToDashboard = () => {
      if (isLogged.value) {
        router.push('/dashboard');
      }
    };

    const logoSrc = computed(() => {
      return store.state.isTheme === 'dark' ? require('@/assets/ShopInfoboardIcon512.svg') : require('@/assets/ShopInfoboardIcon512.svg');
    });

    const currentTheme = computed(() => store.state.isTheme);

    const toggleTheme = () => {
      const currentTheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
      const newTheme = currentTheme === "dark" ? "light" : "dark";
      document.documentElement.classList.toggle("dark");
      store.dispatch('changeTheme', newTheme);
    };

    const isCollapsed = computed(() => store.state.modals.sidebar.isCollapsed);

    const toggleCollapse = () => {
      store.commit('modals/toggleCollapse');
    };

    const isMobile = computed(() => store.state.modals.sidebar.isMobile);

    const isSidebarVisible = computed(() => store.state.modals.sidebar.isSidebarVisible);

    const toggleSidebar = () => {
      store.commit('modals/toggleSidebar');
    };

    return {
      items,
      isLogged,
      user,
      currentShopName,
      logout,
      logoSrc,
      currentTheme,
      toggleTheme,
      userMenu,
      userMenuItems,
      toggleUserMenu,
      goToDashboard,
      isCollapsed,
      toggleCollapse,
      isMobile,
      isSidebarVisible,
      toggleSidebar,
    };
  },
};
</script>

<style>
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 997;
  padding: 0.5rem 0.75rem 0.5rem 0;
  border: 0;
  border-radius: 0;
  border-bottom: 1px solid var(--p-menubar-border-color);
  height: 4rem;
  display: flex;
  align-items: center;
  box-shadow: 0 3px 5px rgba(0, 0, 0, 0.02), 0 0 2px rgba(0, 0, 0, 0.05), 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* Переопределение стилей PrimeVue для menubar */
.topbar.p-menubar {
  border: 0 !important;
  border-bottom: 1px solid var(--p-menubar-border-color) !important;
  border-radius: 0 !important;
}

.sidebar-header {
  display: flex;
  align-items: center;
  position: relative;
  width: 230px;
  min-height: 4rem;
}

.topbar-logo {
  display: block;
}

.mr-2 {
  margin-right: 0.5rem;
}

.ml-2 {
  margin-left: 0.5rem;
}

.mx-1 {
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}

/* Smooth button transitions */
.p-button {
  transition: none;
}

.p-button:hover {
  /* Убираем анимации hover */
}

.p-button:active {
  transition: none;
}

/* Verona-style buttons */
.p-button.p-button-lg .p-button-icon {
  font-size: 1.25rem;
  transition: none;
}

.p-button.p-button-lg:hover .p-button-icon {
  /* Убираем анимации иконок */
}

.p-button.p-button-lg {
  font-size: 1rem;
}

/* Show logo in topbar only on mobile */
@media screen and (max-width: 991px) {
  .topbar-logo {
    display: block;
  }
}

/* Hide sidebar toggle on desktop */
@media screen and (min-width: 992px) {
}

.cursor-pointer {
  cursor: pointer;
}

.sidebar-logo {
  height: 54px;
}
</style>
